import React, { ReactNode } from "react"
import styles from "./ListView.module.css"
import { Loader, Box, Text } from "dash-styled"
import VisibilitySensor from "react-visibility-sensor"
import { NavLink } from "react-router-dom"
import { useBoolean } from "../../../Hooks/useBoolean"

interface ListViewProps<I> {
    data: I[]
    totalCount?: number
    item: (li: I, index: number) => ReactNode
    loadMore?: () => Promise<void>
    unfilterLink?: string
}

export function ListView<I>({
    data,
    item,
    loadMore,
    totalCount,
    unfilterLink
}: ListViewProps<I>): JSX.Element {
    const [isLoadingMore, startLoadingMore, endLoadingMore] = useBoolean(false)
    const renderItem = (li: I, index: number) => {
        const infiniteScollRow = () => {
            const handleChangeVisibility = async (isVisible: boolean) => {
                if (!isLoadingMore && isVisible && loadMore !== undefined) {
                    startLoadingMore()
                    await loadMore()
                    endLoadingMore()
                }
            }

            return (
                <VisibilitySensor key={index} onChange={handleChangeVisibility}>
                    <li className={styles.ListView__itemWrap}>
                        {item(li, index)}
                    </li>
                </VisibilitySensor>
            )
        }

        return loadMore === undefined || index !== data.length - 1 ? (
            <li className={styles.ListView__itemWrap} key={index}>
                {item(li, index)}
            </li>
        ) : (
            infiniteScollRow()
        )
    }

    const endMessage =
        totalCount === undefined ? null : totalCount === data.length ? (
            <Text color="gray">
                Reached the end of the list.
                {unfilterLink === undefined ? null : (
                    <>
                        <br />
                        Are you missing something? Try to{" "}
                        <NavLink to={unfilterLink}>clear filters</NavLink>.
                    </>
                )}
            </Text>
        ) : isLoadingMore ? (
            <Loader />
        ) : null

    return (
        <>
            <ol data-testid="list">{data.map(renderItem)}</ol>
            <Box textAlign="center" paddingTop="4" paddingBottom="7">
                {endMessage}
            </Box>
        </>
    )
}
